import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// Service pages
import BorewellDrilling from './pages/BorewellDrilling';
import BorewellCleaning from './pages/BorewellCleaning';
import SlowRigBorewells from './pages/SlowRigBorewells';
import PumpSalesService from './pages/PumpSalesService';
import SoilTest from './pages/SoilTest';
import PlumbingMaterial from './pages/PlumbingMaterial';
import Gallery from './pages/Gallery';
import PileFoundation from './pages/PileFoundation';
import Dewatering from './pages/Dewatering';

const API = process.env.REACT_APP_API_URL;

/* ─── Dynamic SEO Meta Tags Component ───────────────────────────────── */
function DynamicSEO() {
  useEffect(() => {
    fetch(`${API}/api/site-info`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.data) {
          const { seoTitle, seoDescription, seoKeywords } = d.data;

          // Update title
          if (seoTitle) {
            document.title = seoTitle;
          }

          // Update meta description
          if (seoDescription) {
            let metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) metaDesc.setAttribute('content', seoDescription);

            // Update OG description
            let ogDesc = document.querySelector('meta[property="og:description"]');
            if (ogDesc) ogDesc.setAttribute('content', seoDescription);

            // Update Twitter description
            let twitterDesc = document.querySelector('meta[property="twitter:description"]');
            if (twitterDesc) twitterDesc.setAttribute('content', seoDescription);
          }

          // Update meta title
          if (seoTitle) {
            let metaTitle = document.querySelector('meta[name="title"]');
            if (metaTitle) metaTitle.setAttribute('content', seoTitle);

            // Update OG title
            let ogTitle = document.querySelector('meta[property="og:title"]');
            if (ogTitle) ogTitle.setAttribute('content', seoTitle);

            // Update Twitter title
            let twitterTitle = document.querySelector('meta[property="twitter:title"]');
            if (twitterTitle) twitterTitle.setAttribute('content', seoTitle);
          }

          // Update keywords
          if (seoKeywords) {
            let metaKeywords = document.querySelector('meta[name="keywords"]');
            if (metaKeywords) {
              metaKeywords.setAttribute('content', seoKeywords);
            }
          }
        }
      })
      .catch(() => {});
  }, []);

  return null; // This component doesn't render anything
}

const SERVICES = [
  { label: 'Borewell Drilling', path: '/services/borewell-drilling', icon: '🔩' },
  { label: 'Borewell Cleaning', path: '/services/borewell-cleaning', icon: '🧹' },
  { label: 'Slow Rig Borewells', path: '/services/slow-rig-borewells', icon: '⚙️' },
  { label: 'Pump Sales & Service', path: '/services/pump-sales-service', icon: '🚰' },
  { label: 'Soil Test', path: '/services/soil-test', icon: '🧪' },
  { label: 'Plumbing Material Supply & Erection', path: '/services/plumbing-material', icon: '🔧' },
  { label: 'Pile Foundation', path: '/services/pile-foundation', icon: '🏛️' },
  { label: 'Dewatering', path: '/services/dewatering', icon: '💧' },
];

/* ─── Navbar ─────────────────────────────────────────────────────────── */
function Navbar({ logoUrl }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const handleHomeNav = (anchor) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    } else {
      document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="navbar">
      {/* Brand: logo image if uploaded, else text — always on the left */}
      <Link to="/" className="navbar-brand">
        {logoUrl ? (
          <img src={logoUrl} alt="Elite Borewells Logo" className="navbar-logo" />
        ) : (
          'Elite Borewells'
        )}
      </Link>

      {/* Hamburger */}
      <button
        className={`hamburger ${mobileOpen ? 'open' : ''}`}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
      >
        <span /><span /><span />
      </button>

      <ul className={`navbar-links ${mobileOpen ? 'mobile-open' : ''}`}>
        <li>
          <button className="nav-btn" onClick={() => handleHomeNav('#home')}>Home</button>
        </li>

        {/* Services Dropdown */}
        <li className="dropdown-parent" ref={dropdownRef}>
          <button
            className={`nav-btn dropdown-trigger ${dropdownOpen ? 'active' : ''}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            Services <span className="dropdown-caret">{dropdownOpen ? '▲' : '▼'}</span>
          </button>
          {dropdownOpen && (
            <ul className="dropdown-menu">
              {SERVICES.map((s) => (
                <li key={s.path}>
                  <Link to={s.path} className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                    <span className="dd-icon">{s.icon}</span>
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>

        <li>
          <Link to="/gallery" className="nav-btn nav-link-style">Gallery</Link>
        </li>
        <li>
          <button className="nav-btn" onClick={() => handleHomeNav('#about')}>About</button>
        </li>
        <li>
          <button className="nav-btn" onClick={() => handleHomeNav('#contact')}>Contact</button>
        </li>
      </ul>
    </nav>
  );
}

/* ─── Services Sub-Nav ────────────────────────────────────────────────── */
function ServiceSubNav() {
  const location = useLocation();
  const isServicePage = location.pathname.startsWith('/services/');
  const scrollRef = useRef(null);

  // Scroll active pill into view on mount / route change
  useEffect(() => {
    if (!scrollRef.current) return;
    const active = scrollRef.current.querySelector('.subnav-pill-active');
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [location.pathname]);

  if (!isServicePage) return null;

  return (
    <div className="service-subnav">
      <div className="subnav-inner" ref={scrollRef}>
        {SERVICES.map((s) => (
          <Link
            key={s.path}
            to={s.path}
            className={`subnav-pill${location.pathname === s.path ? ' subnav-pill-active' : ''}`}
          >
            <span className="subnav-icon">{s.icon}</span>
            <span className="subnav-label">{s.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ─── Home Page ───────────────────────────────────────────────────────── */
const SITE_INFO_DEFAULTS = {
  aboutHeading: 'About Elite Borewells',
  aboutText: 'With over 15 years of experience, Elite Borewells is a trusted name in borewell drilling and water solutions across the region. We use modern drilling rigs and follow best practices to ensure safe, reliable, and long-lasting borewells for homes, farms, and industries.',
  aboutPoints: ['15+ Years of Experience', '1000+ Successful Projects', 'Licensed & Certified Team', '24/7 Customer Support'],
  phone: '+91 98765 43210',
  phone2: '',
  email: 'info@eliteborewells.com',
  address: '123 Water Street, Bangalore, Karnataka',
  mapLink: '',
  mapEmbed: '',
  footerText: '© 2025 Elite Borewells. All rights reserved.',
  facebook: '',
  instagram: '',
  youtube: '',
  linkedin: '',
  twitter: '',
};

// Social platform config — SVG paths for crisp icons at any size
const SOCIAL_LINKS = [
  {
    key: 'facebook',
    label: 'Facebook',
    color: '#1877f2',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.885v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
    ),
  },
  {
    key: 'instagram',
    label: 'Instagram',
    color: '#e1306c',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.975 1.246 2.242 1.308 3.608.058 1.265.069 1.645.069 4.851s-.011 3.586-.069 4.851c-.062 1.366-.334 2.633-1.308 3.608-.975.974-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.975-1.246-2.242-1.308-3.608C2.175 15.586 2.163 15.206 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.516 2.497 5.783 2.225 7.15 2.163 8.415 2.105 8.796 2.163 12 2.163zM12 0C8.741 0 8.333.014 7.053.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.053.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.856.601 3.698 1.942 5.039 1.341 1.341 3.183 1.857 5.039 1.942C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 1.856-.085 3.698-.601 5.039-1.942 1.341-1.341 1.857-3.183 1.942-5.039C23.986 15.668 24 15.259 24 12c0-3.259-.014-3.667-.072-4.947-.085-1.857-.601-3.699-1.942-5.04C20.645.673 18.803.157 16.947.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
    ),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    color: '#ff0000',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>
    ),
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    color: '#0a66c2',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
    ),
  },
  {
    key: 'twitter',
    label: 'X / Twitter',
    color: '#000000',
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    ),
  },
];

function HomePage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]); // [{filename, url}, ...]
  const [slideIndex, setSlideIndex] = useState(0);
  const slideTimerRef = useRef(null);
  const [siteInfo, setSiteInfo] = useState(SITE_INFO_DEFAULTS);
  const [homeServices, setHomeServices] = useState(SERVICES.map((s) => ({ ...s, slug: s.path.split('/').pop(), desc: serviceDesc(s.label), imageUrl: null })));

  // Form state
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [formErrors, setFormErrors] = useState({ phone: '', email: '' });

  useEffect(() => {
    fetch(`${API}/api/banners`)
      .then((r) => r.json())
      .then((d) => setBanners(d.banners || []))
      .catch(() => setBanners([]));

    fetch(`${API}/api/site-info`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setSiteInfo(d.data); })
      .catch(() => {});

    fetch(`${API}/api/home-services`)
      .then((r) => r.json())
      .then((d) => { if (d.success && d.services) setHomeServices(d.services); })
      .catch(() => {});
  }, []);

  // Auto-advance slideshow every 4 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    slideTimerRef.current = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(slideTimerRef.current);
  }, [banners.length]);

  const goToSlide = (i) => {
    setSlideIndex(i);
    clearInterval(slideTimerRef.current);
    if (banners.length > 1) {
      slideTimerRef.current = setInterval(() => {
        setSlideIndex((prev) => (prev + 1) % banners.length);
      }, 4000);
    }
  };

  const prevSlide = () => goToSlide((slideIndex - 1 + banners.length) % banners.length);
  const nextSlide = () => goToSlide((slideIndex + 1) % banners.length);

  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return '';

    // Check if it starts with +
    const hasPlus = phone.trim().startsWith('+');

    // Extract only digits
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 0) return '';

    // If starts with +, must be +91 followed by 10 digits
    if (hasPlus) {
      // Check if + is only at the start
      if (phone.indexOf('+') !== phone.lastIndexOf('+')) {
        return 'Invalid format: only one + allowed at the beginning';
      }

      // Must start with +91
      if (!phone.trim().startsWith('+91')) {
        return 'Country code must be +91';
      }

      // After +91, must have exactly 10 digits
      if (digits.length !== 12 || !digits.startsWith('91')) {
        return 'Format: +91 followed by 10 digits';
      }

      return '';
    }

    // If starts with 0, must be 11 digits total (0 + 10 digits)
    if (phone.trim().startsWith('0')) {
      if (digits.length !== 11) {
        return 'Format: 0 followed by 10 digits';
      }
      return '';
    }

    // Otherwise, must be exactly 10 digits
    if (digits.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }

    return '';
  };

  const validateEmail = (email) => {
    if (!email) return ''; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // For phone field, only allow digits, spaces, +, -, and ()
    if (name === 'phone') {
      const sanitized = value.replace(/[^\d\s+\-()]/g, '');
      setFormData((prev) => ({ ...prev, [name]: sanitized }));
      setFormErrors((prev) => ({ ...prev, phone: validatePhone(sanitized) }));
    }
    // For email field, validate format
    else if (name === 'email') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    }
    // For other fields, no special validation
    else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (formStatus === 'sending') {
      return;
    }

    // Validate before submission
    const phoneError = validatePhone(formData.phone);
    const emailError = validateEmail(formData.email);

    if (phoneError || emailError) {
      setFormErrors({ phone: phoneError, email: emailError });
      return;
    }

    setFormStatus('sending');
    try {
      const res = await fetch(`${API}/api/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '' });
        setFormErrors({ phone: '', email: '' });
        setTimeout(() => setFormStatus(null), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus(null), 4000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus(null), 4000);
    }
  };

  return (
    <>
      {/* Hero / Banner Slideshow */}
      <section className="hero" id="home">
        {/* Slide layers */}
        {banners.length > 0 ? (
          banners.map((b, i) => (
            <div
              key={b.filename}
              className={`hero-slide ${i === slideIndex ? 'hero-slide-active' : ''}`}
              style={{ backgroundImage: `url(${b.url})` }}
            />
          ))
        ) : (
          <div className="hero-slide hero-slide-active hero-slide-gradient" />
        )}

        {/* Text overlay */}
        <div className="hero-content hero-content-over-image">
          <h1>Elite Borewells</h1>
          <p>Professional Borewell Drilling &amp; Water Solutions</p>
          <a href="#contact" className="hero-btn">Get a Free Quote</a>
        </div>

        {/* Prev / Next arrows — only when >1 banner */}
        {banners.length > 1 && (
          <>
            <button className="hero-arrow hero-arrow-left" onClick={prevSlide} aria-label="Previous slide">&#8249;</button>
            <button className="hero-arrow hero-arrow-right" onClick={nextSlide} aria-label="Next slide">&#8250;</button>
          </>
        )}

        {/* Dot indicators — only when >1 banner */}
        {banners.length > 1 && (
          <div className="hero-dots">
            {banners.map((_, i) => (
              <button
                key={i}
                className={`hero-dot ${i === slideIndex ? 'hero-dot-active' : ''}`}
                onClick={() => goToSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Services */}
      <section className="section" id="services">
        <h2 className="section-title">Our Services</h2>
        <p className="section-sub">Click on any service to learn more</p>
        <div className="services-grid">
          {homeServices.map((s) => (
            <div
              className={`service-card${s.imageUrl ? ' service-card-has-img' : ''}`}
              key={s.slug || s.label}
              onClick={() => navigate(s.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(s.path)}
            >
              {s.imageUrl ? (
                <div className="service-card-img-wrap">
                  <img src={s.imageUrl} alt={s.label} className="service-card-img" />
                </div>
              ) : (
                <div className="service-icon">{s.icon}</div>
              )}
              <h3>{s.label}</h3>
              <p>{s.desc}</p>
              <span className="service-learn-more">Learn More →</span>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="section section-alt" id="about">
        <div className="about-content">
          <div className="about-text">
            <h2>{siteInfo.aboutHeading}</h2>
            <p>{siteInfo.aboutText}</p>
            <ul className="about-list">
              {(siteInfo.aboutPoints || []).map((pt, i) => (
                <li key={i}>✅ {pt}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section" id="contact">
        <h2 className="section-title">Contact Us</h2>
        <div className="contact-grid">
          <div className="contact-info">
            <div className="contact-item">
              <span className="contact-icon">📞</span>
              <div>
                <strong>Phone</strong>
                {siteInfo.phone && (
                  <p><a href={`tel:${siteInfo.phone}`} className="contact-link">{siteInfo.phone}</a></p>
                )}
                {siteInfo.phone2 && (
                  <p><a href={`tel:${siteInfo.phone2}`} className="contact-link">{siteInfo.phone2}</a></p>
                )}
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📧</span>
              <div>
                <strong>Email</strong>
                {siteInfo.email && (
                  <p><a href={`mailto:${siteInfo.email}`} className="contact-link">{siteInfo.email}</a></p>
                )}
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📍</span>
              <div>
                <strong>Address</strong>
                <p>{siteInfo.address}</p>
                {siteInfo.mapLink && (
                  <a href={siteInfo.mapLink} target="_blank" rel="noopener noreferrer" className="contact-map-btn">
                    📍 Open in Google Maps
                  </a>
                )}
              </div>
            </div>
          </div>
          <form className="contact-form" onSubmit={handleFormSubmit}>
            <input
              type="text" name="name" placeholder="Your Name"
              value={formData.name} onChange={handleFormChange} required
            />
            <div className="form-field">
              <input
                type="tel" name="phone" placeholder="Phone Number"
                value={formData.phone} onChange={handleFormChange} required
                className={formErrors.phone ? 'input-error' : ''}
              />
              {formErrors.phone && <span className="field-error">{formErrors.phone}</span>}
            </div>
            <div className="form-field">
              <input
                type="email" name="email" placeholder="Email Address (optional)"
                value={formData.email} onChange={handleFormChange}
                className={formErrors.email ? 'input-error' : ''}
              />
              {formErrors.email && <span className="field-error">{formErrors.email}</span>}
            </div>
            <textarea
              name="message" placeholder="Your Message" rows={4}
              value={formData.message} onChange={handleFormChange} required
            />
            {formStatus === 'success' && (
              <div className="form-success">Thank you! We will contact you shortly.</div>
            )}
            {formStatus === 'error' && (
              <div className="form-error">Something went wrong. Please try again.</div>
            )}
            <button type="submit" className="form-btn" disabled={formStatus === 'sending'}>
              {formStatus === 'sending' ? 'Sending...' : 'Send Enquiry'}
            </button>
          </form>
        </div>
        {(siteInfo.mapEmbed || siteInfo.mapLink) && (
          <div className="contact-map-embed">
            <iframe
              title="Office Location"
              src={
                siteInfo.mapEmbed
                  ? siteInfo.mapEmbed
                  : `https://maps.google.com/maps?q=${encodeURIComponent(siteInfo.address)}&output=embed`
              }
              width="100%"
              height="340"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="footer">
        {/* Social media icons row — only shown when at least one link is set */}
        {SOCIAL_LINKS.some((s) => siteInfo[s.key]) && (
          <div className="footer-socials">
            {SOCIAL_LINKS.filter((s) => siteInfo[s.key]).map((s) => (
              <a
                key={s.key}
                href={siteInfo[s.key]}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-btn"
                style={{ '--social-color': s.color }}
                title={s.label}
              >
                {s.svg}
              </a>
            ))}
          </div>
        )}

        {/* Contact quick-row in footer */}
        <div className="footer-contact-row">
          {siteInfo.phone && (
            <a href={`tel:${siteInfo.phone}`} className="footer-contact-link">📞 {siteInfo.phone}</a>
          )}
          {siteInfo.phone2 && (
            <a href={`tel:${siteInfo.phone2}`} className="footer-contact-link">📞 {siteInfo.phone2}</a>
          )}
          {siteInfo.email && (
            <a href={`mailto:${siteInfo.email}`} className="footer-contact-link">📧 {siteInfo.email}</a>
          )}
        </div>

        <p className="footer-copy">{siteInfo.footerText}</p>
        <p className="footer-credit">
          Designed &amp; Developed by{' '}
          <a
            href="http://www.development-classiccart.in"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-credit-link"
          >
            Classiccart Pvt Ltd
          </a>
        </p>
      </footer>
    </>
  );
}

function serviceDesc(label) {
  const map = {
    'Borewell Drilling': 'State-of-the-art DTH & rotary drilling rigs for residential, agricultural and industrial borewells.',
    'Borewell Cleaning': "CCTV inspection + air-surging rehabilitation to restore your borewell's original yield.",
    'Slow Rig Borewells': 'Cable tool percussion drilling for hard granite/charnockite rock formations.',
    'Pump Sales & Service': 'Authorized dealer & service centre for Kirloskar, CRI, Grundfos, Texmo pumps.',
    'Soil Test': 'Geotechnical investigation, resistivity survey & agricultural soil analysis.',
    'Plumbing Material Supply & Erection': 'End-to-end plumbing — CPVC/GI pipes, tanks, valves & professional installation.',
    'Pile Foundation': 'Bored cast-in-situ RCC pile foundations for buildings, bridges and heavy infrastructure projects.',
    'Dewatering': 'Groundwater control for construction pits and waterlogged sites using wellpoint and deep well systems.',
  };
  return map[label] || '';
}

/* ─── Scroll to top on route change ──────────────────────────────────── */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* ─── Floating Contact Buttons ───────────────────────────────────────── */
function FloatingContact({ siteInfo }) {
  const phone = siteInfo.phone || siteInfo.phone2 || '';
  const email = siteInfo.email || '';
  const waNumber = phone.replace(/\D/g, '');

  if (!phone && !email) return null;

  return (
    <div className="float-contact">
      {phone && (
        <a
          href={`tel:${phone}`}
          className="float-btn float-call"
          title={`Call ${phone}`}
          aria-label="Call us"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.47 11.47 0 00.57 3.58 1 1 0 01-.25 1.01l-2.2 2.2z"/>
          </svg>
          <span className="float-label">Call</span>
        </a>
      )}
      {waNumber && (
        <a
          href={`https://wa.me/${waNumber}`}
          className="float-btn float-wa"
          target="_blank"
          rel="noopener noreferrer"
          title="WhatsApp"
          aria-label="Chat on WhatsApp"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.531 5.845L0 24l6.347-1.508A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.034-1.388l-.36-.213-3.762.894.951-3.67-.235-.376A9.818 9.818 0 1112 21.818z"/>
          </svg>
          <span className="float-label">WhatsApp</span>
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          className="float-btn float-email"
          title={`Email ${email}`}
          aria-label="Send email"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
          </svg>
          <span className="float-label">Email</span>
        </a>
      )}
    </div>
  );
}

/* ─── Root App ────────────────────────────────────────────────────────── */
function App() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [siteInfo, setSiteInfo] = useState(SITE_INFO_DEFAULTS);

  const fetchLogo = useCallback(() => {
    fetch(`${API}/api/logo`)
      .then((res) => res.json())
      .then((data) => setLogoUrl(data.logoUrl || null))
      .catch(() => setLogoUrl(null));
  }, []);

  useEffect(() => {
    fetchLogo();
    // Re-fetch when the tab becomes visible again (e.g. after uploading in admin)
    const onVisible = () => { if (document.visibilityState === 'visible') fetchLogo(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchLogo);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchLogo);
    };
  }, [fetchLogo]);

  useEffect(() => {
    fetch(`${API}/api/site-info`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setSiteInfo(d.data); })
      .catch(() => {});
  }, []);

  return (
    <Router>
      <DynamicSEO />
      <ScrollToTop />
      <div className="customer-root">
        <Navbar logoUrl={logoUrl} />
        <ServiceSubNav />
        <FloatingContact siteInfo={siteInfo} />

        <div className="page-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services/borewell-drilling" element={<BorewellDrilling />} />
            <Route path="/services/borewell-cleaning" element={<BorewellCleaning />} />
            <Route path="/services/slow-rig-borewells" element={<SlowRigBorewells />} />
            <Route path="/services/pump-sales-service" element={<PumpSalesService />} />
            <Route path="/services/soil-test" element={<SoilTest />} />
            <Route path="/services/plumbing-material" element={<PlumbingMaterial />} />
            <Route path="/services/pile-foundation" element={<PileFoundation />} />
            <Route path="/services/dewatering" element={<Dewatering />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="*" element={
              <div style={{ textAlign: 'center', padding: '80px 20px', color: '#1a3a5c' }}>
                <h2>Page not found</h2>
                <Link to="/" style={{ color: '#2b4c7e' }}>← Back to Home</Link>
              </div>
            } />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
